import React, { useEffect, useMemo, useState } from "react";
import {
  Backdrop,
  CircularProgress,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Switch,
  FormControl,
  FormLabel,
  Autocomplete,
  IconButton,
  Button,
  Box,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MDButton from "components/MDButton";
import DataTable from "comps/Tables/DataTable";
import TLinkApi from "services/tlink.api";
import MRTClientDataTable from "comps/Tables/DataTable/MRTClientDataTable";
import { MRT_ShowHideColumnsButton, MRT_ToggleFiltersButton, MRT_ToggleFullScreenButton } from "material-react-table";

const BASE_ENDPOINT = "/configurations/supplier-client-buyer";
const LANG_ENDPOINT = "/masters/language";

const oneLine = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

const parseAllowedLanguageId = (raw, languages) => {
  if (raw === -1 || raw === "-1") return -1;
  if (typeof raw === "string") {
    const s = raw.trim();
    if (/^all\s+lan?guages?$/i.test(s)) return -1;
    const asNum = Number(s);
    if (Number.isFinite(asNum)) return asNum;
    const byName = languages.find((l) => l.name.toLowerCase() === s.toLowerCase());
    if (byName) return byName.id;
  }
  if (Number.isFinite(Number(raw))) return Number(raw);
  return -1;
};

const ClientBuyerConfiguration = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [data, setData] = useState([]);

  // modal + form
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("add");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [editingId, setEditingId] = useState(1); // prefer API id if provided

  // languages catalog
  const [languages, setLanguages] = useState([]);
  const languageOptions = useMemo(
    () => [{ id: -1, name: "All Languages" }, ...languages],
    [languages]
  );
  const languageMap = useMemo(
    () => Object.fromEntries(languages.map((l) => [l.id, l.name])),
    [languages]
  );

  // FORM (strictly per contract + isActive extra)
  const [supplierId, setSupplierId] = useState("");
  const [clientId, setClientId] = useState("");
  const [buyerId, setBuyerId] = useState(""); // optional
  const [languageId, setLanguageId] = useState(-1);
  const [isAllowed, setIsAllowed] = useState(true);
  const [isActive, setIsActive] = useState(true); // independent property to send in payload

  const fetchLanguages = async () => {
    try {
      const res = await TLinkApi.get(LANG_ENDPOINT);
      const list =
        (res?.data ?? [])
          .map((x) => ({ id: Number(x.id), name: String(x.name) }))
          .sort((a, b) => a.name.localeCompare(b.name));
      setLanguages(list);
    } catch (e) {
      console.log(e);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await TLinkApi.get(BASE_ENDPOINT);
      const rows = (res?.data ?? []).map((r, idx) => ({
        id: r.id ?? `${r.supplier_id}:${r.client_id}:${r.buyer_id ?? ""}:${idx}`,
        supplier_id: r.supplier_id,
        supplier_name: r.supplier_name,
        client_id: r.client_id,
        client_name: r.client_name,
        buyer_id: r.buyer_id ?? null,
        buyer_name: r.buyer_name ?? "",
        allowed_buyers: r.allowed_buyers ?? "", // read-only display if provided
        allowed_languages: r.allowed_languages ?? "", // can be "All Lanaguages" or id
        is_allowed: !!r.is_allowed, // read-only display
      }));
      setData(rows);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLanguages();
    fetchData();
  }, []);

  const openAdd = () => {
    setMode("add");
    setEditingId(null);
    setSupplierId("");
    setClientId("");
    setBuyerId("");
    setLanguageId(-1);
    setIsAllowed(false);
    setIsActive(true);
    setErrorMsg("");
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setMode("edit");
    setEditingId(Number.isFinite(Number(row.id)) ? Number(row.id) : null);
    setSupplierId(String(row.supplier_id ?? ""));
    setClientId(String(row.client_id ?? ""));
    setBuyerId(row.buyer_id != null ? String(row.buyer_id) : "");
    setLanguageId(parseAllowedLanguageId(row.allowed_languages, languages));
    setIsAllowed(Boolean(row.is_allowed));
    // isActive is not linked to GET; keep default or last selection (choose default true here)
    setIsActive(true);
    setErrorMsg("");
    setModalOpen(true);
  };

  const valid = supplierId.trim() !== "" && clientId.trim() !== "";

  const handleSave = async () => {
    if (!valid) {
      setErrorMsg("Supplier ID and Client ID are required.");
      return;
    }
    setSubmitting(true);
    setErrorMsg("");
    try {
      const payload = {
        ...(mode === "edit" && editingId != null ? { id: editingId } : {}),
        supplierId: Number(supplierId),
        clientId: Number(clientId),
        buyerId: buyerId.trim() ? Number(buyerId) : null,
        languageId: Number(languageId),
        isAllowed: Boolean(isAllowed),
        isActive: Boolean(isActive),
      };

      if (mode === "add") {
        await TLinkApi.post(BASE_ENDPOINT, payload);
      } else {
       
          await TLinkApi.put(`${BASE_ENDPOINT}/1`, payload);
       
      }

      setModalOpen(false);
      await fetchData();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Something went wrong";
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessor: "supplier_id",
        Header: "Supplier Id",
        align: "left",
        Cell: ({ row }) => {
          const v = row?.original?.supplier_id;
          return <div>{(v === null || v === undefined || v === "") ? "-" : v}</div>;
        },
      },
      {
        accessor: "supplier_name",
        Header: "Supplier Name",
        align: "left",
        sortable: true,
        Cell: ({ row }) => {
          const name = row?.original?.supplier_name || "";
          const display = name ? (name.length > 20 ? name.slice(0, 20) + "..." : name) : "-";
          return (
            <Tooltip title={name && name.length > 20 ? name : ""}>
              <div style={oneLine}>{display}</div>
            </Tooltip>
          );
        },
      },
      {
        accessor: "client_id",
        Header: "Client Id",
        align: "left",
        Cell: ({ row }) => {
          const v = row?.original?.client_id;
          return <div>{(v === null || v === undefined || v === "") ? "-" : v}</div>;
        },
      },
      {
        accessor: "client_name",
        Header: "Client Name",
        align: "left",
        sortable: true,
        Cell: ({ row }) => {
          const name = row?.original?.client_name || "";
          const display = name ? (name.length > 20 ? name.slice(0, 20) + "..." : name) : "-";
          return (
            <Tooltip title={name && name.length > 20 ? name : ""}>
              <div style={oneLine}>{display}</div>
            </Tooltip>
          );
        },
      },
      { accessor: "buyer_id", Header: "Buyer Id", align: "left", Cell: ({ row }) => { const v = row?.original?.buyer_id; return <div>{(v === null || v === undefined || v === "") ? "-" : v}</div>; }, },
      {
        accessor: "buyer_name",
        Header: "Buyer Name",
        align: "left",
        sortable: true,
        Cell: ({ row }) => {
          const name = row?.original?.buyer_name || "";
          const truncated = name
            ? (name.length > 20 ? name.substring(0, 20) + "..." : name)
            : "-";
          return (
            <Tooltip title={name && name.length > 20 ? name : ""}>
              <div style={oneLine}>{truncated}</div>
            </Tooltip>
          );
        },
      },
      {
        accessor: "allowed_buyers",
        Header: "Allowed Buyers",
        align: "left",
        headerAlign: "left",
        Cell: ({ row }) => {
          const raw = row.original.allowed_buyers;
          const display =
            typeof raw === "string" && raw.trim()
              ? raw
              : Array.isArray(raw)
                ? raw.join(", ")
                : "-";
          return (
            <Tooltip title={display}>
              <div style={oneLine}>{display}</div>
            </Tooltip>
          );
        },
      },
      {
        accessor: "allowed_languages",
        Header: "Allowed Languages",
        align: "center",
        headerAlign: "center",
        Cell: ({ row }) => {
          const raw = row.original.allowed_languages;
          let display = "-";
          if (
            raw
            // === -1 ||
            // raw === "-1" ||
            // (typeof raw === "string" && /^all\s+lan?guages?$/i.test(raw.trim()))
          ) {
            display = raw;
          }
          // else {
          //   const id = parseAllowedLanguageId(raw, languages);
          //   display = languageMap[id] ?? String(id);
          // }
          return (
            <Tooltip title={display}>
              <div style={oneLine}>{display}</div>
            </Tooltip>
          );
        },
      },
      {
        accessor: "is_allowed",
        Header: "Allowed",
        align: "left",
        headerAlign: "left",
        width: 50,
        Cell: ({ row }) => (row.original.is_allowed ? "Yes" : "No"),
      },
      // {
      //   accessor: "is_allowed",
      //   Header: "Allowed?",
      //   align: "left",
      //   width: 50,
      //   Cell: ({ row }) => (row.original.is_allowed ? "Yes" : "No"),
      // },
      {
        accessor: "actions",
        Header: "Actions",
        align: "right",
        headerAlign: "right",
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end" pr={1}>
            <IconButton size="small" onClick={() => openEdit(row.original)} sx={{ p: 0.5 }}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [languages, languageMap]
  );
  const [fullScr, setFullScr] = useState(false);
  const renderToolbarInternalActions = ({ table }) => {
    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: "0rem" }}>
          <Button variant="contained" color="info" onClick={openAdd}>
            + New
          </Button>
          <MRT_ShowHideColumnsButton table={table} />
          <MRT_ToggleFiltersButton table={table} />
          <div onClick={() => setFullScr(!fullScr)} >
            <MRT_ToggleFullScreenButton table={table} />
          </div>
        </div>
      </>
    );
  };

  return (
    <div>
      {isLoading ? (
        <Backdrop sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }} open={isLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : null}

      {/* <DataTable
        table={{ columns, rows: data }}
        isSorted
        entriesPerPage={entriesPerPage}
        canSearch={false}
        tablePage={page}
        setTablePage={setPage}
        setEntriesPerPage={setEntriesPerPage}
        totalLength={data.length}
        pagination
        enableTopToolbar
        tableMaxHeight="calc(100vh - 220px)"
        tableMinHeight="calc(100vh - 220px)"
        renderTopToolbarCustomActions={() => (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: window.innerWidth > 1600 ? "calc(76vw - 580px)" : "calc(76vw - 565px)",
              marginTop: "3px",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "black" }}>
              Client Buyer Configuration
            </Typography>
            <Button variant="contained" color="info" onClick={openAdd}>
              + New
            </Button>
          </Box>
        )}
      /> */}

      <MRTClientDataTable
        table={{ columns, rows: data }}
        isSorted
        canSearch={false}
        enableTopToolbar
        defaultPageSize={entriesPerPage}           // initial page size
        entriesOption={["10", "20", "30", "40", "50"]}
        tableMaxHeight="calc(100vh - 220px)"
        tableMinHeight="calc(100vh - 220px)"
        renderToolbarInternalActions={renderToolbarInternalActions}
        fullScr={fullScr}
        renderTopToolbarCustomActions={() => (
          <Box
          // sx={{
          //   display: "flex",
          //   justifyContent: "space-between",
          //   alignItems: "center",
          //   width:
          //     window.innerWidth > 1600
          //       ? "calc(76vw - 580px)"
          //       : "calc(76vw - 565px)",
          //   mt: "3px",
          // }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "black" }}>
              Client Buyer Configuration
            </Typography>
            {/* <Button variant="contained" color="info" onClick={openAdd}>
              + New
            </Button> */}
          </Box>
        )}
      />

      {/* Add / Edit Modal */}
      <Dialog open={modalOpen} onClose={() => !submitting && setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{mode === "add" ? "Add" : "Update"}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Supplier ID</FormLabel>
                <TextField
                  size="small"
                  type="number"
                  value={supplierId}
                  onChange={(e) => setSupplierId(e.target.value)}
                />
              </FormControl>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Client ID</FormLabel>
                <TextField
                  size="small"
                  type="number"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                />
              </FormControl>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Buyer ID (optional)</FormLabel>
                <TextField
                  size="small"
                  type="number"
                  value={buyerId}
                  onChange={(e) => setBuyerId(e.target.value)}
                />
              </FormControl>
            </Stack>

            <FormControl fullWidth>
              <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Language</FormLabel>
              <Autocomplete
                options={languageOptions}
                value={languageOptions.find((opt) => opt.id === languageId) ?? languageOptions[0]}
                onChange={(_, selected) => setLanguageId(selected?.id ?? -1)}
                getOptionLabel={(opt) => opt?.name ?? ""}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField {...params} size="small" placeholder="Select one language (or All)" />
                )}
              />
            </FormControl>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
              <FormControl>
                <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Allowed</FormLabel>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Switch checked={isAllowed} onChange={(e) => setIsAllowed(e.target.checked)} />
                  <Typography variant="body2">{isAllowed ? "Yes" : "No"}</Typography>
                </Stack>
              </FormControl>


            </Stack>

            {errorMsg ? (
              <Typography variant="body2" color="error">
                {errorMsg}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={() => setModalOpen(false)} disabled={submitting}>
            Cancel
          </MDButton>
          <MDButton variant="contained" color="info" onClick={handleSave} disabled={submitting || !valid}>
            {submitting ? (mode === "add" ? "Saving…" : "Updating…") : mode === "add" ? "Save" : "Update"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ClientBuyerConfiguration;
